import React, { useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  CustomLoading,
  NetworkError,
  NotFoundError,
  PageError,
  ServerError,
} from "@ippon/shared-components";

import {
  type ApiErrorType,
  normalizeDisplayApiErrorType,
} from "@/utils/apiErrorPayload";
import { getPostLoginRestoreFallback } from "@/utils/postLoginRedirect";

export interface InitialLoadErrorProps {
  errorType: ApiErrorType | null | undefined;
  onRetry?: () => void;
}

const InitialLoadError: React.FC<InitialLoadErrorProps> = ({
  errorType,
  onRetry,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const displayErrorType = normalizeDisplayApiErrorType(errorType);
  const restoreFallback = getPostLoginRestoreFallback(
    displayErrorType,
    location.state,
  );
  const restoreFallbackMessage = restoreFallback?.message;

  useEffect(() => {
    if (!restoreFallbackMessage) return;
    navigate("/home", {
      replace: true,
      state: {
        showToast: {
          type: "warning",
          message: restoreFallbackMessage,
        },
      },
    });
  }, [navigate, restoreFallbackMessage]);

  if (restoreFallbackMessage) {
    return <CustomLoading loading={true} fullScreen size="medium" />;
  }

  if (displayErrorType === "notFound") {
    return <NotFoundError />;
  }

  if (displayErrorType === "forbidden") {
    return (
      <PageError
        actionLabel="ホームへ"
        message="このページを表示する権限がありません。"
        onRetry={() => navigate("/home")}
        statusCode="403"
        title="ページにアクセスできません"
      />
    );
  }

  if (displayErrorType === "network") {
    return <NetworkError actionLabel="再読み込み" onRetry={onRetry} />;
  }

  if (displayErrorType === "unknown") {
    return (
      <PageError
        actionLabel="再読み込み"
        message="ページの読み込みに失敗しました。再度お試しください。"
        onRetry={onRetry}
        title="予期しないエラーが発生しました"
      />
    );
  }

  return <ServerError actionLabel="再読み込み" onRetry={onRetry} />;
};

export default InitialLoadError;
